import React, { useEffect, useState } from 'react';
import { getProjectStages } from '../services/projectService';
import { getChecklists, createInspection, cloneChecklistToProject } from '../services/qualityService';
import { createStage, updateStageStatus, reorderStage, deleteStage } from '../services/stageService';
import { getEmployees } from '../services/employeeService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';

const STATUS_COLORS = {
  pending: 'bg-gray-200 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  done: 'bg-green-100 text-green-800',
};

const initialNewStage = { name: '', requires_inspection: false, linked_checklist_id: '' };

const ProductionPlanPanel = ({ projectId }) => {
  const [stages, setStages] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(false);
  const [newStage, setNewStage] = useState(initialNewStage);
  const [inspectorPicks, setInspectorPicks] = useState({});
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    Promise.all([
      getProjectStages(projectId),
      // Checklists already cloned to this project, PLUS reusable templates -
      // templates aren't tied to any project, so filtering by project_id
      // alone (the old behavior) left nothing to pick for any project that
      // hadn't already had a checklist manually cloned to it via the
      // Quality > Checklists page. A template gets auto-cloned on selection
      // (see handleAddStage) so this dropdown is never a dead end.
      getChecklists({ project_id: projectId }),
      getChecklists({ is_template: true }),
      getEmployees(),
    ])
      .then(([stageRes, projectChecklistRes, templateRes, empRes]) => {
        setStages(stageRes.data.stages || []);
        const projectChecklists = projectChecklistRes.data.checklists || [];
        const templates = templateRes.data.checklists || [];
        setChecklists([...projectChecklists, ...templates]);
        setEmployees(empRes.data.employees || []);
      })
      .catch(() => showToast('Failed to load production plan', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddStage = async () => {
    if (!newStage.name.trim()) {
      showToast('Stage name is required', 'error');
      return;
    }
    try {
      let linkedChecklistId = newStage.linked_checklist_id ? Number(newStage.linked_checklist_id) : null;
      const picked = checklists.find((c) => c.id === linkedChecklistId);
      // A template isn't usable as-is (it's not tied to any project) - clone
      // it into a project-scoped checklist first, then link the clone.
      if (picked?.is_template) {
        const cloneRes = await cloneChecklistToProject(picked.id, { project_id: Number(projectId) });
        linkedChecklistId = cloneRes.data.checklist_id;
      }
      await createStage({
        project_id: Number(projectId),
        name: newStage.name,
        requires_inspection: newStage.requires_inspection,
        linked_checklist_id: linkedChecklistId,
      });
      showToast('Stage added', 'success');
      setNewStage(initialNewStage);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add stage', 'error');
    }
  };

  const handleStart = async (stage) => {
    try {
      await updateStageStatus(stage.id, 'in_progress');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to start stage', 'error');
    }
  };

  const handleMarkDone = async (stage) => {
    try {
      await updateStageStatus(stage.id, 'done');
      showToast(`${stage.name} marked done`, 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Cannot mark stage done', 'error');
    }
  };

  const handleCallInspection = async (stage) => {
    const inspectorId = inspectorPicks[stage.id];
    if (!inspectorId) {
      showToast('Select an inspector first', 'error');
      return;
    }
    try {
      const checklist = checklists.find((c) => c.id === stage.linked_checklist_id);
      await createInspection({
        project_id: Number(projectId),
        project_stage_id: stage.id,
        inspection_type: checklist?.checklist_type || 'intermediate',
        inspector_id: Number(inspectorId),
        checklist_id: stage.linked_checklist_id || null,
      });
      showToast('Inspection called', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to call inspection', 'error');
    }
  };

  const handleRemove = async (stage) => {
    if (!window.confirm(`Remove stage "${stage.name}"?`)) return;
    try {
      await deleteStage(stage.id);
      showToast('Stage removed', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to remove stage', 'error');
    }
  };

  const handleReorder = async (stage, direction) => {
    try {
      await reorderStage(stage.id, direction);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to reorder stage', 'error');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading production plan...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          Ordered milestones for this project's production, with inspection gates where required.
        </p>
        <button onClick={() => setManaging(!managing)} className="text-komal-blue hover:underline text-xs font-semibold whitespace-nowrap">
          {managing ? 'Done managing' : 'Manage stages'}
        </button>
      </div>

      {stages.length === 0 ? (
        <p className="text-sm text-gray-400">No production plan defined yet.</p>
      ) : (
        <ol className="space-y-2">
          {stages.map((s, idx) => {
            const checklistIncomplete =
              s.checklist_summary && s.checklist_summary.checked_count < s.checklist_summary.total_count;
            return (
              <li key={s.id} className={`border rounded p-3 ${s.is_blocked ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-400 font-mono">#{idx + 1}</span>
                    <span className="font-medium text-sm">{s.name}</span>
                    <span className={`text-xs px-2 py-1 rounded font-semibold ${STATUS_COLORS[s.status]}`}>
                      {s.status.replace('_', ' ')}
                    </span>
                    {s.is_blocked && (
                      <span className="text-xs px-2 py-1 rounded font-semibold bg-red-100 text-red-800">
                        Blocked — needs inspection
                      </span>
                    )}
                  </div>
                  {managing && (
                    <div className="flex gap-2 text-xs">
                      <button onClick={() => handleReorder(s, 'up')} className="text-gray-500 hover:text-gray-700">↑</button>
                      <button onClick={() => handleReorder(s, 'down')} className="text-gray-500 hover:text-gray-700">↓</button>
                      {s.status === 'pending' && (
                        <button onClick={() => handleRemove(s)} className="text-red-600 hover:underline">Remove</button>
                      )}
                    </div>
                  )}
                </div>

                {s.requires_inspection && s.checklist_summary && (
                  <div className="text-xs text-gray-500 mt-1">
                    Checklist "{s.checklist_summary.checklist_name}": production {s.checklist_summary.production_checked_count ?? 0}/{s.checklist_summary.total_count} · quality {s.checklist_summary.checked_count}/{s.checklist_summary.total_count} verified
                    {s.latest_inspection_status && ` · Inspection: ${s.latest_inspection_status}`}
                  </div>
                )}

                {s.requires_inspection && s.inspection_history?.length > 0 && (
                  <div className="mt-1">
                    <button
                      onClick={() => setExpandedHistoryId(expandedHistoryId === s.id ? null : s.id)}
                      className="text-xs text-komal-blue hover:underline"
                    >
                      {expandedHistoryId === s.id ? 'Hide' : 'Show'} QC verification history ({s.inspection_history.length})
                    </button>
                    {expandedHistoryId === s.id && (
                      <ul className="mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                        {s.inspection_history.map((insp) => (
                          <li key={insp.id} className="text-xs text-gray-500">
                            <span className={`font-semibold ${insp.status === 'failed' ? 'text-red-600' : insp.status === 'pending' ? 'text-gray-500' : 'text-green-700'}`}>
                              {insp.status.replace('_', ' ')}
                            </span>
                            {' — '}{insp.inspection_type} by {insp.inspector_name || 'unknown'} on {formatDateDDMMYY(insp.inspection_date)}
                            {insp.findings && <span className="block italic">"{insp.findings}"</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {s.status === 'pending' && (
                    <button onClick={() => handleStart(s)} className="text-xs px-2 py-1 rounded bg-komal-blue text-white font-semibold">
                      Start
                    </button>
                  )}
                  {s.status === 'in_progress' && s.requires_inspection && (!s.latest_inspection_status || s.latest_inspection_status === 'failed') && (
                    <>
                      <select
                        value={inspectorPicks[s.id] || ''}
                        onChange={(e) => setInspectorPicks({ ...inspectorPicks, [s.id]: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1 text-xs"
                      >
                        <option value="">Select inspector...</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                      <button
                        onClick={() => handleCallInspection(s)}
                        disabled={checklistIncomplete}
                        title={checklistIncomplete ? 'Checklist must be fully checked off first' : ''}
                        className="text-xs px-2 py-1 rounded bg-komal-blue text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {s.latest_inspection_status === 'failed' ? 'Call Re-Inspection' : 'Call Inspection'}
                      </button>
                    </>
                  )}
                  {s.status === 'in_progress' && (
                    <button onClick={() => handleMarkDone(s)} className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 font-semibold">
                      Mark Done
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {managing && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Stage</div>
          <div className="flex gap-2 flex-wrap items-center">
            <input
              placeholder="Stage name"
              value={newStage.name}
              onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={newStage.requires_inspection}
                onChange={(e) => setNewStage({ ...newStage, requires_inspection: e.target.checked })}
              />
              Requires inspection
            </label>
            {newStage.requires_inspection && (
              <select
                value={newStage.linked_checklist_id}
                onChange={(e) => setNewStage({ ...newStage, linked_checklist_id: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 text-xs"
              >
                <option value="">Link a checklist (optional)</option>
                {checklists.some((c) => !c.is_template) && (
                  <optgroup label="This project">
                    {checklists.filter((c) => !c.is_template).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                )}
                {checklists.some((c) => c.is_template) && (
                  <optgroup label="Templates (will be copied to this project)">
                    {checklists.filter((c) => c.is_template).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            <button onClick={handleAddStage} className="text-xs px-3 py-1 rounded bg-komal-blue text-white font-semibold">
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionPlanPanel;
